import { LooseKeys, UseFormReturnType } from "@mantine/form";

import { format, isValid, parse, parseISO } from "date-fns";

export type MantineDateInputValue = string | Date | null;

function setFormFieldValue<T>(
  form: UseFormReturnType<T>,
  path: LooseKeys<T>,
  value: unknown,
): void {
  (form as unknown as UseFormReturnType<Record<string, unknown>>).setFieldValue(path, value);
}

/**
 * Normalises Mantine v8 DateInput onChange values to a local-midnight Date.
 * Date-only strings use parseISO (local), not UTC midnight parsing.
 */
export function parseMantineDateInputValue(value: string | Date): Date;
export function parseMantineDateInputValue(value: null): null;
export function parseMantineDateInputValue(value: MantineDateInputValue): Date | null;
export function parseMantineDateInputValue(value: MantineDateInputValue): Date | null {
  if (value === null) return null;
  return typeof value === "string" ? parseISO(value) : value;
}

/**
 * Parses a stored date string (MM/dd/yyyy or yyyy-MM-dd) for Mantine DateInput display.
 */
export function parseDisplayDateString(
  raw: string | null | undefined,
  displayFormat = "MM/dd/yyyy",
): Date | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const isoDate = parseISO(trimmed);
    return isValid(isoDate) ? isoDate : null;
  }

  const parsed = parse(trimmed, displayFormat, new Date());
  return isValid(parsed) ? parsed : null;
}

/** Formats a Mantine DateInput onChange value as a display string (default MM/dd/yyyy). */
export function formatMantineDateInputValue(
  value: MantineDateInputValue,
  displayFormat = "MM/dd/yyyy",
): string {
  if (value === null) return "";
  return format(parseMantineDateInputValue(value), displayFormat);
}

/** onChange handler that stores a Mantine DateInput value as a formatted date string. */
export function createMantineDateInputStringChangeHandler<T, Field extends LooseKeys<T>>(
  form: UseFormReturnType<T>,
  fieldName: Field,
  displayFormat = "MM/dd/yyyy",
) {
  return (value: MantineDateInputValue) => {
    setFormFieldValue(form, fieldName, formatMantineDateInputValue(value, displayFormat));
  };
}

/** onChange handler that stores a Mantine DateInput value as a Date on the form. */
export function createMantineDateInputChangeHandler<T, Field extends LooseKeys<T>>(
  form: UseFormReturnType<T>,
  fieldName: Field,
) {
  return (value: MantineDateInputValue) => {
    setFormFieldValue(
      form,
      fieldName,
      value === null ? undefined : parseMantineDateInputValue(value),
    );
  };
}
